import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePotwierdzenie } from '@/components/OknoPotwierdzenia';
import {
  AlertTriangle,
  Database,
  DatabaseBackup,
  Download,
  Eraser,
  FileUp,
  HardDrive,
  Loader2,
  ShieldQuestion,
  Trash2,
  Upload,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  bazaZNazwyPaczki,
  fetchMachineTasks,
  listAgents,
  usunKopie,
  wgrajKopie,
  wyczyscKopieBezPliku,
  wyczyscNieudaneKopie,
  zlecKopieBazy,
  zlecKopieLokalna,
  przerwijZadanie,
  zlecOdtworzenie,
  zlecPobranieKopii,
  zlecUsuniecieKopiiLokalnej,
  zlecWyslanieKopii,
  type AgentRow,
  type MachineTask,
} from '@/lib/agents-api';
import { KopiaPortalu } from '@/components/KopiaPortalu';
import { celWgrania } from '@/lib/cel-wgrania';
import { czyPokazacKopie, DNI_BEZ_PLIKU_NA_EKRANIE, porownajNazwyWezlow } from '@/lib/kopie-widok';
import { paczkiNaDysku } from '@/lib/kopie-na-dysku';
import {
  fetchPoczekalnia,
  przejmijPaczke,
  usunZPoczekalni,
  wgrajDoPoczekalni,
  type PaczkaPoczekalni,
} from '@/lib/poczekalnia-api';
import { odswiezSpisKopii } from '@/lib/sync-tables-api';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';

/**
 * KOPIE ZAPASOWE BAZY SPS — zlecanie, przegląd i pobranie w JEDNYM miejscu.
 *
 * ⚠️ POWSTAŁO Z BRAKU, KTÓRY ZGŁOSIŁ UŻYTKOWNIK: kopię dało się zlecić tylko przyciskiem
 * schowanym przy agencie, a potem nie było widać ANI ile tych kopii jest, ANI jak pobrać plik
 * na komputer. Kopia, o której nie wiadomo, czy istnieje, nie jest kopią zapasową — jest
 * nadzieją. Ekran odpowiada więc na trzy pytania naraz: co mamy, z kiedy i skąd to wziąć.
 *
 * ⚠️ ODŚWIEŻAMY, DOPÓKI COŚ TRWA. Kopia zajmuje od kilkunastu sekund do kilku minut, więc bez
 * odpytywania człowiek patrzy na martwy ekran i klika drugi raz — a drugie zlecenie to druga
 * kopia na dysku maszyny klienta.
 */
export function Kopie() {
  const { t } = useI18n();
  const { zapytaj, oknoPytania } = usePotwierdzenie();
  const toast = useToast();
  const qc = useQueryClient();
  const { groupId } = useIslands();
  const [agentId, setAgentId] = useState<string | null>(null);
  const [doUsuniecia, setDoUsuniecia] = useState<MachineTask | null>(null);

  const agenci = useQuery({ queryKey: ['agents'], queryFn: () => listAgents() });

  const zadania = useQuery({
    queryKey: ['machine-tasks', groupId],
    queryFn: () => fetchMachineTasks(groupId!),
    enabled: Boolean(groupId),
    refetchInterval: (q) =>
      (q.state.data ?? []).some((z) => z.status !== 'DONE' && z.status !== 'FAILED') ? 3_000 : false,
  });

  /**
   * CZY LISTA KOPII SIĘ WCZYTUJE — a nie „czy zapytanie stoi w `pending`".
   *
   * ⚠️⚠️ ZAPYTANIE WYŁĄCZONE MA W REACT-QUERY STAN `pending` NA ZAWSZE, i to jest drugi objaw
   * zgłoszenia z 09.09.2026: w projekcie bez agentów `groupId` jest `null`, zapytanie nigdy nie
   * startuje, a ekran do końca świata pisze „Ładowanie…". Na zrzucie ze zgłoszenia widać dokładnie
   * to zdanie pod nagłówkiem „Zrobione kopie".
   *
   * ⚠️ BEZ WYSPY LISTA JEST PUSTA I TO JEST PRAWDA, KTÓRĄ ZNAMY — zadania maszynowe wiszą na
   * wyspie, więc gdy jej nie ma, nie ma ich skąd wziąć. Ekran mówi więc „brak kopii", a kartę
   * wyżej tłumaczy, że wyspy jeszcze nie ma. Wieczne „Ładowanie…" udawało pracę, której nie było.
   */
  const zadaniaWczytuja = groupId !== null && zadania.isPending && !zadania.isError;

  /**
   * Kopię robi agent pilnujący BAZY — agent samego automatu nie ma czego kopiować.
   *
   * ⚠️ POSORTOWANE PO NAZWIE, KTÓRĄ WIDAĆ NA LIŚCIE (`kopie-widok.ts`) — zgłoszone: „lista nie jest
   * alfabetyczna". Szła kolejnością z API, czyli kolejnością rejestracji agentów.
   *
   * ⚠️ SORTUJEMY PO ETYKIECIE, NIE PO `nodeId`. Na liście stoi nazwa automatu, a `nodeId` widać
   * tylko u węzła bez automatu — sortowanie po polu, którego nie widać, wyglądałoby jak brak
   * sortowania.
   */
  const etykietaWezla = (a: { machine?: { name: string } | null; nodeId: string }) => a.machine?.name ?? a.nodeId;
  const bazowi = (agenci.data?.agents ?? [])
    .filter((a) => a.kind === 'DATABASE' && a.group.id === groupId)
    .sort((x, y) => porownajNazwyWezlow(etykietaWezla(x), etykietaWezla(y)));
  const wybrany = agentId ?? bazowi[0]?.id ?? null;

  /**
   * KOPIE LEŻĄCE W MAGAZYNIE — z OBU dróg, którymi tam trafiają.
   *
   * ⚠️ `KOPIA_WYSLIJ` MUSI TU BYĆ. Kopia wysłana z dysku maszyny ląduje w magazynie dokładnie tak
   * samo jak zrobiona zdalnie i podlega temu samemu limitowi pięciu — ale filtr po samym
   * `KOPIA_BAZY` nie pokazałby jej na liście. Plik byłby w magazynie, zajmowałby miejsce w limicie
   * i nie dałoby się go ani pobrać, ani skasować z ekranu.
   *
   * ⚠️ `KOPIA_WGRANA` TAK SAMO — paczka od operatora leży w tym samym magazynie i w tym samym
   * limicie. Trzeci rodzaj w tym filtrze to trzecia okazja do zapomnienia, dlatego po stronie API
   * ta sama lista jest JEDNĄ stałą (`W_MAGAZYNIE`).
   */
  const kopie = (zadania.data ?? []).filter(
    (z) => z.kind === 'KOPIA_BAZY' || z.kind === 'KOPIA_WYSLIJ' || z.kind === 'KOPIA_WGRANA',
  );

  /**
   * KOPIE DO POKAZANIA — bez wierszy po plikach, których już nie ma i które przestały cokolwiek
   * znaczyć. Reguła i jej uzasadnienie: `lib/kopie-widok.ts`.
   *
   * ⚠️ LICZYMY, ILE UKRYLIŚMY, I MÓWIMY TO NA EKRANIE. Lista, która po cichu pokazuje mniej, niż
   * ma, jest gorsza od długiej listy: następne pytanie brzmi „to gdzie się podziała tamta kopia",
   * a odpowiedź „ukryliśmy ją, bo była stara" musi stać obok, nie w cudzej głowie.
   *
   * ⚠️ `kopie` (pełna lista) ZOSTAJE do liczników — `doPobrania` i zestaw sum kontrolnych opisują
   * MAGAZYN, a nie to, co widać. Podmiana ich na listę widoczną znaczyłaby, że ukrycie wiersza
   * zmienia stan magazynu.
   */
  const teraz = new Date();
  const widoczne = kopie.filter((z) => czyPokazacKopie(z, teraz));
  const ukrytych = kopie.length - widoczne.length;
  /**
   * ⚠️ LICZYMY Z PEŁNEJ LISTY, NIE Z WIDOCZNEJ. Przycisk sprząta WSZYSTKIE wiersze bez pliku —
   * także te już ukryte — więc liczba na nim musi mówić, ile naprawdę zniknie. Liczba wzięta
   * z listy widocznej obiecywałaby mniej, niż operacja robi.
   */
  const bezPliku = kopie.filter((z) => z.status === 'DONE' && z.output === null).length;

  /**
   * CZY COKOLWIEK WOKÓŁ KOPII WŁAŚNIE TRWA — i to ze WSZYSTKICH czterech dróg, nie z jednej.
   *
   * ⚠️ POPRAWKA PO ZGŁOSZENIU (13.08.2026): warunek liczył tylko `KOPIA_BAZY`, więc w trakcie kopii
   * NA DYSK oba przyciski zostawały aktywne i dało się zlecić drugą. Każde z tych zadań zajmuje tę
   * samą maszynę i ten sam katalog, a agent bierze JEDNO zadanie na raz — więc drugie kliknięcie
   * nie przyspiesza niczego, tylko dokłada kopię do kolejki i miejsca na dysku klienta.
   */
  const wTrakcie = (zadania.data ?? []).find(
    (z) =>
      (z.kind === 'KOPIA_BAZY' ||
        z.kind === 'KOPIA_LOKALNA' ||
        z.kind === 'KOPIA_WYSLIJ' ||
        z.kind === 'KOPIA_POBIERZ' ||
        /** Odtwarzanie zajmuje maszynę na minuty tak samo jak kopia — i tak samo blokuje resztę. */
        z.kind === 'KOPIA_ODTWORZ') &&
      z.status !== 'DONE' &&
      z.status !== 'FAILED',
  );
  const trwa = wTrakcie !== undefined;

  /**
   * CZY TRWAJĄCE ZADANIE JUŻ NIE TRWA, TYLKO WISI.
   *
   * ⚠️ PRÓG NA MINUTACH, NIE NA STANIE ZADANIA, bo zawieszone i pracujące wyglądają w bazie
   * IDENTYCZNIE — `RUNNING` z ostatnim postępem sprzed kwadransa to wciąż `RUNNING`. Pięć minut
   * z zapasem przekracza każdą kopię, jaką widzieliśmy na warsztacie (104 MB w 7 s, 1,4 GB w kilka
   * minut), więc alarm nie zapala się przy normalnej pracy.
   *
   * ⚠️ CISZA AGENTA JEST TU FAKTEM ROZSTRZYGAJĄCYM. Zadanie wiszące przy ŻYWYM agencie może się
   * jeszcze dokończyć; przy agencie milczącym od kwadransa nie dokończy się nigdy, a portal i tak
   * czeka pełne 30 minut, zanim odda je do kolejki.
   */
  const PROG_UTKNIECIA_MIN = 5;
  const minutOd = (kiedy: string): number => Math.floor((Date.now() - new Date(kiedy).getTime()) / 60_000);
  const agentZadania = wTrakcie === undefined ? null : (agenci.data?.agents ?? []).find((a) => a.id === wTrakcie.agent.id);
  const minutTrwania = wTrakcie === undefined ? 0 : minutOd(wTrakcie.createdAt);
  const utknelo =
    wTrakcie !== undefined && minutTrwania >= PROG_UTKNIECIA_MIN
      ? {
          zadanie: wTrakcie,
          minut: minutTrwania,
          ciszaMinut: agentZadania?.lastSeenAt ? minutOd(agentZadania.lastSeenAt) : null,
        }
      : null;

  /**
   * WGRANIE PACZKI Z KOMPUTERA OPERATORA.
   *
   * ⚠️ NIE PODLEGA BLOKADZIE `trwa` I TO NIE JEST PRZEOCZENIE. Wysyłka idzie z przeglądarki prosto
   * do magazynu — maszyna nie bierze w niej udziału, więc nie ma powodu czekać na zadanie, które
   * akurat na niej trwa. Blokada istnieje po to, żeby nie zlecać agentowi dwóch rzeczy naraz.
   */
  const plikRef = useRef<HTMLInputElement>(null);
  const [wysylka, setWysylka] = useState<{ nazwa: string; procent: number } | null>(null);
  const [paczkaDoUsuniecia, setPaczkaDoUsuniecia] = useState<PaczkaPoczekalni | null>(null);

  /**
   * ═════════════════════════════════════════════════════════════════════
   *  DWIE DROGI JEDNEGO PRZYCISKU — rozstrzyga to, czy wyspa ma WĘZEŁ BAZY.
   *
   *  ⚠️⚠️ ZAMÓWIONE 08.09.2026: „czy możemy dodać, by backup z komputera można było wrzucać na
   *  zasób backupowy, bez wyspy? żeby był gotówy jak ona powstanie".
   *
   *  ⚠️⚠️ JEDEN PRZYCISK, NIE DWA. Wgranie na wyspę i na półkę to dla człowieka ta sama
   *  czynność („wrzuć tę kopię do portalu"), a różnica jest w tym, czy jest już gdzie ją przypisać.
   *  Dwa przyciski kazałyby wybierać między nimi na podstawie stanu, który portal zna lepiej.
   *  Podpis pod przyciskiem mówi, gdzie paczka wyląduje — wybór jest jawny, tylko nie do klikania.
   *
   *  ⚠️⚠️ PRZYPIĘCIE LICZY `celWgrania`, A NIE `groupId!` — i to jest POPRAWKA BŁĘDU ZGŁOSZONEGO
   *  09.09.2026 (Kamil): „nie może wstawić pliku bak do proja bez agentów". Projekt bez agentów
   *  nie ma wyspy, więc `groupId` jest `null`; wykrzyknik uciszal `tsc`, a `URLSearchParams`
   *  zamieniał `undefined` na napis `"undefined"` i API odmawiało zdaniem o nieprawidłowym
   *  UUID-zie. Poczekalnia nie działała dokładnie w tym jednym przypadku, dla którego powstała.
   * ═════════════════════════════════════════════════════════════════════
   */
  const wgraj = useMutation({
    mutationFn: (plik: File) =>
      wybrany === null
        ? wgrajDoPoczekalni(celWgrania(groupId), plik, (procent) => setWysylka({ nazwa: plik.name, procent }))
        : /**
           * ⚠️ TU `groupId` JEST PEWNE, nie na wiarę: `wybrany` bierze się z `bazowi`, a ta lista
           * jest filtrowana po `a.group.id === groupId`. Węzeł wybrany bez wyspy nie istnieje.
           */
          wgrajKopie(wybrany, groupId!, plik, (procent) => setWysylka({ nazwa: plik.name, procent })),
    onSuccess: async (r) => {
      setWysylka(null);
      toast.success(t('kopie.wgrana').replace('{plik}', r.nazwa));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
      await qc.invalidateQueries({ queryKey: ['poczekalnia', groupId] });
    },
    onError: (e: Error) => {
      setWysylka(null);
      toast.error(e.message);
    },
  });

  /**
   * PÓŁKA — paczki wgrane z komputera, które jeszcze nie są kopią żadnej wyspy.
   *
   * ⚠️ PYTAMY Z `groupId`, żeby serwer sam rozstrzygnął, które z nich czekają na TĘ wyspę —
   * wprost albo przez kod założycielski, który ją założył. Ta druga droga jest całym mechanizmem
   * „gotowa, jak wyspa powstanie" i ekran nie ma jej skąd policzyć sam.
   *
   * ⚠️⚠️ BEZ `enabled` — I TO JEST CZĘŚĆ POPRAWKI ZGŁOSZENIA Z 09.09.2026. Bramka
   * `enabled: Boolean(groupId)` znaczyła, że w projekcie bez agentów ta lista NIGDY nie ruszała,
   * a to jedyne miejsce, w którym świeżo wgrana paczka ma się pokazać. API tej trasy `groupId`
   * nie wymaga: bez niego oddaje wszystko, co widzi konto, z `dlaTejWyspy: null`.
   */
  const polka = useQuery({
    queryKey: ['poczekalnia', groupId],
    queryFn: () => fetchPoczekalnia(groupId ?? undefined),
    staleTime: 30_000,
  });

  const przejmij = useMutation({
    mutationFn: (id: string) => przejmijPaczke(id, groupId!, wybrany!),
    onSuccess: async (r) => {
      toast.success(t('poczek.przejeta').replace('{plik}', r.nazwa));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
      await qc.invalidateQueries({ queryKey: ['poczekalnia', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const usunPaczke = useMutation({
    mutationFn: (id: string) => usunZPoczekalni(id),
    onSuccess: async () => {
      toast.success(t('poczek.usunieta'));
      await qc.invalidateQueries({ queryKey: ['poczekalnia', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * CO POKAZUJEMY W POCZEKALNI TEJ WYSPY.
   *
   * ⚠️⚠️ PACZKI TEJ WYSPY **I** TE ZE WSPÓLNEJ PÓŁKI — tak, żeby na świeżej wyspie było co
   * wprowadzić. Kopia wgrana przed wdrożeniem często nie ma jeszcze do czego się przypiąć
   * (kod założycielski wydaje SDI, a wgrać może ktoś inny), więc ukrycie półki znaczyłoby,
   * że paczka leży w magazynie i nie ma jej gdzie zobaczyć.
   *
   * ⚠️ PACZKI PRZYPIĘTE DO INNEJ WYSPY TU NIE WCHODZĄ. To jedyne miejsce, w którym pomyłka
   * kończy się cudzą bazą na maszynie tej wyspy — więc nie stawiamy jej w zasięgu kliknięcia.
   */
  const czekajace = useMemo(
    () =>
      (polka.data ?? []).filter(
        (paczka) => paczka.przejeta === null && (paczka.dlaTejWyspy === true || paczka.przypiecie.rodzaj === 'POLKA'),
      ),
    [polka.data],
  );

  const zlec = useMutation({
    mutationFn: () => zlecKopieBazy(wybrany!, groupId!),
    onSuccess: async () => {
      toast.success(t('kopia.zlecona'));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * ⚠️ OSOBNY PRZYCISK, NIE PRZEŁĄCZNIK PRZY TYM WYŻEJ. Kopia lokalna i zdalna kończą się
   * w innych miejscach i mają inne warunki odmowy (wolne miejsce na maszynie kontra limit pięciu
   * w magazynie). Jeden przycisk z opcją znaczyłby, że o skutku decyduje stan pola, którego
   * przy klikaniu nikt nie sprawdza.
   */
  /**
   * ⚠️ SUMY KOPII LEŻĄCYCH NA MASZYNIE — po nich chowamy przycisk „wrzuć na komputer" przy pozycji,
   * którą maszyna już ma. Bierzemy je z wybranego agenta, bo to na JEGO dysk trafiłby plik;
   * wspólny zbiór dla całej wyspy chowałby przycisk na maszynie, która tej kopii nie ma.
   */
  const naMaszynie = new Set(
    (bazowi.find((a) => a.id === wybrany)?.localBackups ?? []).map((k) => k.suma),
  );

  /**
   * ⚠️ WYJŚCIE Z ZAKLESZCZENIA. Blokada przycisków trzyma je do końca trwającego zadania, więc
   * zadanie zawieszone w połowie wysyłki unieruchamia cały ekran aż do przeterminowania (30 min).
   * Ten przycisk mówi „przestań na to czekać" — agenta nie zatrzymuje, ale jego spóźniony wynik
   * zostanie odrzucony po statusie.
   */
  const przerwij = useMutation({
    mutationFn: (zadanieId: string) => przerwijZadanie(zadanieId, groupId!),
    onSuccess: async () => {
      toast.success(t('kopie.przerwano'));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const pobierzNaMaszyne = useMutation({
    mutationFn: (zadanieId: string) => zlecPobranieKopii(wybrany!, groupId!, zadanieId),
    onSuccess: async () => {
      toast.success(t('kopie.pobranieZlecone'));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const zlecLokalna = useMutation({
    mutationFn: () => zlecKopieLokalna(wybrany!, groupId!),
    onSuccess: async () => {
      toast.success(t('kopia.zleconaLokalna'));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * POBRANIE KOPII — zwykły odnośnik prosto do API.
   *
   * ⚠️ NIE MA JUŻ KROKU „POPROŚ O LINK". Magazyn przestał być publiczny (`ODPOWIEDZI.md`, A),
   * więc plik i tak idzie przez API — a skoro tak, to nie ma po co pytać o podpisany adres,
   * którego nikt już nie wystawia. Ciasteczko sesji jedzie z odnośnikiem samo, a nazwę pliku
   * podaje serwer nagłówkiem `Content-Disposition`.
   *
   * ⚠️ ZWYKŁY ODNOŚNIK, NIE `fetch` + Blob. Pobranie przez `fetch` wciągnęłoby całą paczkę
   * do pamięci karty, zanim cokolwiek trafi na dysk — przy kopii bazy to dziesiątki megabajtów
   * bez powodu. Przeglądarka robi to strumieniem sama.
   */
  const pobierz = (id: string) => {
    const a = document.createElement('a');
    a.href = `/api/machine-tasks/${id}/plik?groupId=${encodeURIComponent(groupId!)}`;
    a.rel = 'noopener';
    a.click();
  };

  const usun = useMutation({
    mutationFn: (id: string) => usunKopie(id, groupId!),
    onSuccess: async () => {
      setDoUsuniecia(null);
      toast.success(t('kopie.usunieta'));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const doPobrania = kopie.filter((z) => z.status === 'DONE' && z.output !== null).length;

  /**
   * ⚠️ LICZYMY NIEUDANE ZE WSZYSTKICH ZADAŃ KOPII, nie tylko z tych widocznych w tabeli wyżej.
   * Nieudane kasowanie z dysku albo ściąganie na maszynę też zostawia wiersz — po prostu na tej
   * liście go nie widać, więc bez tego przycisk milczałby o śmieciach, które istnieją.
   */
  const nieudanych = (zadania.data ?? []).filter((z) => z.status === 'FAILED' && z.kind.startsWith('KOPIA_')).length;

  /**
   * SPRZĄTNIĘCIE WPISÓW BEZ PLIKU NA ŻĄDANIE — zgłoszone: „nie chcę, żeby coś wisiało tydzień, jak
   * ktoś to świadomie kliknął". Automatyczne ukrycie po tygodniu zostaje dla resztek, o których
   * nikt nie pamięta; ten przycisk jest dla kogoś, kto właśnie posprzątał i chce to widzieć.
   */
  const sprzatnij = useMutation({
    mutationFn: () => wyczyscKopieBezPliku(groupId!),
    onSuccess: async (r) => {
      toast.success(t('kopie.sprzatnieto').replace('{n}', String(r.usunieto)));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const wyczysc = useMutation({
    mutationFn: () => wyczyscNieudaneKopie(groupId!),
    onSuccess: async (r) => {
      toast.success(t('kopie.wyczyszczono').replace('{n}', String(r.usunieto)));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="max-w-6xl space-y-5">
      {oknoPytania}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('nav.backups')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('kopie.podtytul')}</p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-5">
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground" htmlFor="agent-kopii">
              {t('kopie.maszyna')}
            </label>
            <Select
              id="agent-kopii"
              className="h-9 w-72"
              value={wybrany ?? ''}
              onChange={(e) => setAgentId(e.target.value)}
              disabled={bazowi.length === 0}
            >
              {bazowi.map((a) => (
                <option key={a.id} value={a.id}>
                  {etykietaWezla(a)}
                </option>
              ))}
            </Select>
          </div>
          <Button disabled={wybrany === null || zlec.isPending || trwa} onClick={() => zlec.mutate()}>
            {zlec.isPending || trwa ? <Loader2 className="animate-spin" /> : <Database />}
            {t('kopia.zlec')}
          </Button>
          <Button
            variant="outline"
            disabled={wybrany === null || zlecLokalna.isPending || trwa}
            onClick={() => zlecLokalna.mutate()}
            title={t('kopia.zlecLokalnaOpis')}
          >
            {zlecLokalna.isPending || trwa ? <Loader2 className="animate-spin" /> : <HardDrive />}
            {t('kopia.zlecLokalna')}
          </Button>
          {/*
            WGRANIE PACZKI Z KOMPUTERA. ⚠️ Ukryte pole pliku, bo natywny `<input type="file">` nie da
            się zrobić przyciskiem w stylu reszty ekranu — a to jest ta sama akcja co dwie obok.
          */}
          <input
            ref={plikRef}
            type="file"
            accept=".zip,.bak"
            className="hidden"
            onChange={(e) => {
              const plik = e.target.files?.[0];
              /** ⚠️ Czyścimy pole OD RAZU — inaczej wybranie tego samego pliku drugi raz nic nie zrobi. */
              e.target.value = '';
              if (plik !== undefined) wgraj.mutate(plik);
            }}
          />
          {/*
            ⚠️⚠️ TEN PRZYCISK NIE WYMAGA WĘZŁA, w odróżnieniu od dwóch obok. Plik idzie
            przeglądarka → magazyn i agenta do tego nie potrzebuje — agent wchodzi do gry dopiero
            przy ściąganiu paczki na maszynę. Bez węzła paczka ląduje w POCZEKALNI tej wyspy
            i czeka tam na pierwszą maszynę (zamówione 08.09.2026).
          */}
          <Button
            variant="outline"
            disabled={wgraj.isPending}
            onClick={() => plikRef.current?.click()}
            title={wybrany === null ? t('poczek.wgrajOpis') : t('kopie.wgrajOpis')}
          >
            {wgraj.isPending ? <Loader2 className="animate-spin" /> : <FileUp />}
            {wybrany === null ? t('poczek.wgraj') : t('kopie.wgraj')}
          </Button>
          {/*
            ⚠️ POWÓD BLOKADY NAPISANY WPROST. Sam wyszarzony przycisk nie mówi, czy coś trwa, czy
            czegoś brakuje — a przy kopii idącej kilka minut to jest jedyne pytanie, jakie ma się
            wtedy w głowie. Podajemy też, KTÓRE zadanie trwa, bo cztery różne prowadzą do tej samej
            blokady.
          */}
          {trwa && wTrakcie !== undefined && (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t(`kopie.trwa.${wTrakcie.kind}`)}
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs"
                disabled={przerwij.isPending}
                onClick={() => przerwij.mutate(wTrakcie.id)}
                title={t('kopie.przerwijOpis')}
              >
                {t('kopie.przerwij')}
              </Button>
            </span>
          )}

          {/*
            ⚠️⚠️ ZADANIE, KTÓRE STOI ZA DŁUGO — CAŁA SZEROKOŚĆ, NIE SZARY DOPISEK OBOK.
            Zgłoszone z DBW (13.08.2026): „cały czas wisi zawieszona kopia, minęła godzina i dalej
            nic nie da się zrobić". Wyjście z zakleszczenia BYŁO — przycisk „Przerwij" obok kręcącego
            się kółka — ale wyglądał jak podpis do animacji, a nie jak jedyna rzecz, którą trzeba
            kliknąć. Po kilku minutach mówimy więc WPROST: ile to trwa, kiedy agent ostatni raz się
            odezwał i co z tym zrobić.
          */}
          {utknelo !== null && (
            <div className="flex w-full flex-wrap items-center gap-3 rounded-md border border-[var(--destructive)]/40 bg-[var(--destructive)]/5 px-3 py-2">
              <AlertTriangle className="size-4 shrink-0 text-[var(--destructive)]" />
              <div className="min-w-0 flex-1 text-xs">
                <p className="font-medium">{t('kopie.utknelo').replace('{minut}', String(utknelo.minut))}</p>
                {/* Fakt rozstrzygający: martwy agent nie dokończy zadania ANI ZA GODZINĘ. */}
                <p className="text-muted-foreground">
                  {utknelo.ciszaMinut === null
                    ? t('kopie.utknelo.bezAgenta')
                    : t('kopie.utknelo.cisza').replace('{minut}', String(utknelo.ciszaMinut))}
                </p>
              </div>
              <Button
                size="sm"
                variant="destructive"
                disabled={przerwij.isPending}
                onClick={() => przerwij.mutate(utknelo.zadanie.id)}
                title={t('kopie.przerwijOpis')}
              >
                {przerwij.isPending && <Loader2 className="animate-spin" />}
                {t('kopie.przerwij')}
              </Button>
            </div>
          )}
          {/*
            ⚠️⚠️ KOMUNIKAT MÓWI TEŻ, CO WOLNO. Sam „ta wyspa nie ma agenta bazy" jest prawdą,
            która kończy rozmowę — a od 08.09.2026 wgrać kopię można nawet wtedy: ląduje
            w poczekalni i czeka na pierwszą maszynę.
          */}
          {/*
            ⚠️ „TA WYSPA NIE MA AGENTA BAZY" JEST NIEPRAWDĄ, GDY WYSPY NIE MA WCALE — a to jest
            właśnie stan z zgłoszenia z 09.09.2026 („proj bez agentów"). Zdanie o brakującym
            agencie każe szukać agenta; zdanie o brakującej wyspie mówi, na co się czeka.
            Druga połowa zostaje ta sama, bo jest prawdziwa w obu przypadkach.
          */}
          {bazowi.length === 0 && !agenci.isPending && (
            <p className="w-full text-sm text-muted-foreground">
              {groupId === null ? t('poczek.brakWyspy') : t('kopie.brakAgenta')} {t('poczek.brakAgentaAle')}
            </p>
          )}

          {/*
            POSTĘP WYSYŁKI Z KOMPUTERA — pasek, nie kółko. ⚠️ Paczka idzie kilka minut przez łącze
            klienta, a kręcące się kółko nie odróżnia „jedzie" od „zawiesiło się". Procent bierze
            się z rzeczywistej liczby wysłanych bajtów (`xhr.upload.onprogress`), więc mówi prawdę.
          */}
          {wysylka !== null && (
            <div className="w-full space-y-1">
              <div className="flex items-baseline justify-between text-xs">
                <span className="min-w-0 truncate text-muted-foreground">
                  {t('kopie.wgrywanie').replace('{plik}', wysylka.nazwa)}
                </span>
                <span className="tabular-nums">{wysylka.procent}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${wysylka.procent}%` }} />
              </div>
              {/* ⚠️ 100% ≠ GOTOWE: bajty są w API, ale magazyn jeszcze nie potwierdził zapisu. */}
              {wysylka.procent >= 100 && <p className="text-xs text-muted-foreground">{t('kopie.wgrywanieKoniec')}</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/*
        ════════════════════════════════════════════════════════════════════
         POCZEKALNIA — PACZKI, KTÓRE NIE SĄ JESZCZE KOPIĄ ŻADNEJ WYSPY.

         ⚠️⚠️ NAD LISTĄ KOPII, NIE POD NIĄ. To rzeczy CZEKAJĄCE NA RUCH człowieka; lista niżej
         jest historią. Schowanie ich pod historią znaczyłoby, że paczka wsiana przed wdrożeniem
         czeka tam, gdzie nikt nie patrzy.

         ⚠️ PRZEJĘTYCH TU NIE POKAZUJEMY — są już na liście kopii poniżej, jako zwykłe wpisy.
         Dwa wiersze o jednym pliku mówiłyby to samo dwa razy i zostawiały pytanie, który z nich
         jest tym prawdziwym.
        ════════════════════════════════════════════════════════════════════
      */}
      {czekajace.length > 0 && (
        <Card>
          <CardContent className="pt-5">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <h2 className="text-sm font-semibold">{t('poczek.tytul')}</h2>
              <span className="text-xs text-muted-foreground">
                {t('poczek.ile').replace('{ile}', String(czekajace.length))}
              </span>
            </div>
            <ul className="space-y-2">
              {czekajace.map((paczka) => (
                <li
                  key={paczka.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-border bg-muted/20 px-3 py-2"
                >
                  <span className="min-w-0 flex-1 truncate font-mono text-xs">{paczka.nazwa}</span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {paczka.rozmiar === null ? '—' : mb(paczka.rozmiar)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(paczka.createdAt).toLocaleString('pl-PL')}
                  </span>
                  {/*
                    ⚠️⚠️ PRZYPIĘCIE WIDOCZNE PRZY KAŻDEJ PACZCE. „Dla tej wyspy" a „leży na wspólnej
                    półce" to różnica, która rozstrzyga, czy wolno ją tu wprowadzić bez namysłu —
                    a przy dwóch wdrożeniach naraz nikt tego nie pamięta z głowy.
                  */}
                  {paczka.przypiecie.rodzaj === 'KOD' ? (
                    <Badge variant="neutral" title={t('poczek.zKoduOpis')}>
                      {t('poczek.zKodu').replace('{wyspa}', paczka.przypiecie.nazwa)}
                    </Badge>
                  ) : paczka.przypiecie.rodzaj === 'POLKA' ? (
                    <Badge variant="neutral" title={t('poczek.zPolkiOpis')}>
                      {t('poczek.zPolki')}
                    </Badge>
                  ) : null}
                  {paczka.error !== null && (
                    <span className="flex items-center gap-1 text-xs text-[var(--destructive)]">
                      <AlertTriangle className="size-3 shrink-0" aria-hidden />
                      {paczka.error}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    {/*
                      ⚠️⚠️ POWÓD BLOKADY W DYMKU, ZAWSZE. Trzy różne rzeczy prowadzą do tego samego
                      wyszarzenia (brak węzła bazy, brak pliku, brak sumy kontrolnej), a każda ma
                      inne wyjście. Sam wyszarzony przycisk kazałby zgadywać, którą z nich jest.
                    */}
                    <Button
                      size="sm"
                      disabled={paczka.powodOdmowy !== null || wybrany === null || przejmij.isPending}
                      onClick={() => przejmij.mutate(paczka.id)}
                      title={paczka.powodOdmowy ?? (wybrany === null ? t('poczek.brakWezla') : t('poczek.przejmijOpis'))}
                    >
                      {przejmij.isPending ? <Loader2 className="animate-spin" /> : <Upload />}
                      {t('poczek.przejmij')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[var(--destructive)]"
                      disabled={usunPaczke.isPending}
                      onClick={() => setPaczkaDoUsuniecia(paczka)}
                      title={t('poczek.usunOpis')}
                    >
                      <Trash2 />
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-5">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-semibold">{t('kopie.lista')}</h2>
            <span className="flex items-baseline gap-3">
              {/*
                ⚠️ SPRZĄTANIE NIEUDANYCH — zgłoszone z DBW: „wciąż mamy czerwone śmieci".
                Nieudany wpis nie miał ŻADNEGO przycisku (kosz pojawia się tylko przy gotowej
                kopii z plikiem), więc ekran po każdej nieudanej próbie robił się o jeden
                czerwony wiersz dłuższy i zostawał taki na zawsze.
              */}
              {nieudanych > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                  disabled={wyczysc.isPending}
                  onClick={() => wyczysc.mutate()}
                  title={t('kopie.wyczyscOpis')}
                >
                  {wyczysc.isPending ? <Loader2 className="animate-spin" /> : <Eraser className="size-3.5" />}
                  {t('kopie.wyczysc').replace('{n}', String(nieudanych))}
                </Button>
              )}
              {/*
                ⚠️ DRUGI PRZYCISK SPRZĄTAJĄCY, i to nie to samo co „Wyczyść nieudane": tam giną
                wiersze po próbach, które nic nie zrobiły, tu wiersze po kopiach, które BYŁY, a ich
                plik już nie istnieje. Rozdzielone celowo — jedno kliknięcie na oba znaczyłoby,
                że sprzątanie śmieci kasuje przy okazji historię udanych kopii.
              */}
              {bezPliku > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                  disabled={sprzatnij.isPending}
                  title={t('kopie.sprzatnijOpis').replace('{d}', String(DNI_BEZ_PLIKU_NA_EKRANIE))}
                  onClick={async () => {
                    /* ⚠️ POTWIERDZENIE, bo wiersza nie da się przywrócić — a niesie on datę i maszynę. */
                    const tytul = t('kopie.sprzatnij').replace('{n}', String(bezPliku));
                    const tresc = t('kopie.sprzatnijPytanie').replace('{n}', String(bezPliku));
                    if (!(await zapytaj({ tytul, tresc, etykietaPotwierdz: tytul, ton: 'grozne' }))) return;
                    sprzatnij.mutate();
                  }}
                >
                  {sprzatnij.isPending ? <Loader2 className="animate-spin" /> : <Eraser className="size-3.5" />}
                  {t('kopie.sprzatnij').replace('{n}', String(bezPliku))}
                </Button>
              )}
              {/* Sedno ekranu jedną liczbą: ile kopii DA SIĘ dziś pobrać, a nie ile ich kiedyś zrobiono. */}
              <span className="text-xs text-muted-foreground">
                {t('kopie.doPobrania').replace('{n}', String(doPobrania))}
              </span>
            </span>
          </div>

          {zadaniaWczytuja && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {/* ⚠️ BEZ TEJ GAŁĘZI NIEUDANE ZAPYTANIE WYGLĄDA JAK WIECZNE ŁADOWANIE. Tak właśnie
              objawił się brak jednego rodzaju zadania w schemacie: 200 z API, wywrócona walidacja
              i ekran, który w nieskończoność mówi „Ładowanie…". Cisza jest gorsza niż błąd. */}
          {zadania.isError && (
            <p className="text-sm text-destructive">
              {t('common.error')}: {(zadania.error as Error).message}
            </p>
          )}
          {!zadaniaWczytuja && !zadania.isError && widoczne.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('kopie.brak')}</p>
          )}
          {/*
            ⚠️ LISTA PEŁNA WPISÓW BEZ PLIKU TO INNY STAN NIŻ LISTA PUSTA, a wygląda podobnie:
            widać wiersze, a przy żadnym nie ma ani jednego przycisku (akcje pojawiają się wyłącznie
            przy pozycji z plikiem). Bez tego zdania ekran sugeruje, że wysyłania na maszynę
            w ogóle nie ma — i dokładnie tak został odczytany.
          */}
          {kopie.length > 0 && doPobrania === 0 && (
            <p className="mt-2 text-xs text-muted-foreground">{t('kopie.zadnaDoPobrania')}</p>
          )}
          {/*
            ⚠️ UKRYCIE MUSI BYĆ POWIEDZIANE, i to jest cały powód, dla którego ta linijka istnieje.
            Lista pokazująca po cichu mniej, niż ma, rodzi pytanie „to gdzie się podziała tamta
            kopia" — a odpowiedź musi stać obok listy, nie w cudzej głowie. Liczba PO dwukropku,
            żeby nie odmieniać rzeczownika w dwóch językach.
          */}
          {ukrytych > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              {t('kopie.ukryte').replace('{n}', String(ukrytych)).replace('{d}', String(DNI_BEZ_PLIKU_NA_EKRANIE))}
            </p>
          )}

          {widoczne.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="py-2 pr-3 font-medium">{t('kopie.kiedy')}</th>
                    <th className="py-2 pr-3 font-medium">{t('kopie.maszyna')}</th>
                    {/*
                      ⚠️ NAZWA PLIKU I SUMA TAKŻE PO STRONIE MAGAZYNU — dotąd były tylko przy
                      kopiach na maszynie. Bez nich nie dało się na oczy porównać, czy to TA SAMA
                      paczka po obu stronach, a przecież właśnie po sumie rozstrzyga się, czy
                      wysyłać ją w którąkolwiek stronę.
                    */}
                    <th className="py-2 pr-3 font-medium">{t('kopie.plik')}</th>
                    <th className="py-2 pr-3 font-medium">{t('kopie.stan')}</th>
                    <th className="py-2 pr-3 text-right font-medium">{t('kopie.rozmiar')}</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {widoczne.map((z) => {
                    const out = z.output as { size?: number; suma?: string; nazwa?: string } | null;
                    return (
                      <tr key={z.id} className="border-b border-border/60 last:border-0">
                        <td className="py-2 pr-3 tabular-nums">
                          {new Date(z.finishedAt ?? z.createdAt).toLocaleString('pl-PL')}
                        </td>
                        <td className="py-2 pr-3">
                          {/*
                            ⚠️ POCHODZENIE PACZKI, NIE TYLKO MASZYNA. Kopia zrobiona przez agenta
                            i plik wgrany z laptopa dają w magazynie ten sam artefakt, ale zupełnie
                            inną gwarancję: o pierwszej wiemy, z której bazy i z kiedy pochodzi,
                            o drugiej wiemy tyle, ile powiedział człowiek, który ją wgrał.
                          */}
                          {z.kind === 'KOPIA_WGRANA' ? (
                            <span className="flex items-center gap-1.5">
                              <FileUp className="size-3.5 shrink-0 text-muted-foreground" />
                              {t('kopie.zKomputera')}
                            </span>
                          ) : (
                            (z.agent.machine?.name ?? z.agent.nodeId)
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          {out?.nazwa === undefined ? (
                            /** Wpis bez pliku — nieudany albo przycięty limitem; nie ma czego nazwać. */
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <>
                              <code className="text-xs">{out.nazwa}</code>
                              {out.suma !== undefined && (
                                /* Skrót sumy — do porównania z kopią na maszynie bez wchodzenia w szczegóły. */
                                <span className="ml-2 text-[11px] text-muted-foreground" title={out.suma}>
                                  {out.suma.slice(0, 8)}
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <StanKopii z={z} />
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {out?.size === undefined ? '—' : `${Math.round(out.size / 1_048_576)} MB`}
                        </td>
                        <td className="py-2 text-right">
                          {z.status === 'DONE' && z.output !== null && (
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => pobierz(z.id)}
                              >
                                <Download />
                                {t('kopia.pobierz')}
                              </Button>
                              {/*
                                ⚠️⚠️ PRZYCISK JEST ZAWSZE WIDOCZNY, A POWÓD BLOKADY NAPISANY WPROST.
                                Poprzednia wersja ZNIKAŁA w trzech różnych sytuacjach (maszyna ma już
                                tę kopię, kopia bez sumy kontrolnej, nie wybrano maszyny) i była do
                                tego samą ikoną bez podpisu — obok „Pobierz", które podpis ma.
                                Skutek zgłoszony wprost: „nadal nie mamy pusha backupów z serwera na
                                maszynę", choć funkcja działała od dnia. Brak przycisku wygląda
                                identycznie jak brak funkcji, a to dwie zupełnie różne rzeczy.
                              */}
                              <NaMaszyne
                                powod={
                                  wybrany === null
                                    ? 'brakMaszyny'
                                    : out?.suma === undefined
                                      ? 'brakSumy'
                                      : naMaszynie.has(out.suma)
                                        ? 'juzTam'
                                        : trwa
                                          ? 'trwa'
                                          : null
                                }
                                pracuje={pobierzNaMaszyne.isPending && pobierzNaMaszyne.variables === z.id}
                                onKlik={() => pobierzNaMaszyne.mutate(z.id)}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label={t('kopie.usun')}
                                title={t('kopie.usun')}
                                onClick={() => setDoUsuniecia(z)}
                              >
                                <Trash2 />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <KopieLokalne
        agent={bazowi.find((a) => a.id === wybrany) ?? null}
        /**
         * ⚠️ ZADANIA IDĄ W DÓŁ, bo spis agenta NIE JEST jedynym źródłem prawdy o tym katalogu —
         * uzasadnienie w `kopie-na-dysku.ts`. Paczkę, którą portal sam tam położył zadaniem, zna
         * z własnego wpisu i musi ją pokazać także wtedy, gdy maszyna nie zdążyła (albo nie ma jak)
         * przysłać pomiaru.
         */
        zadania={zadania.data ?? []}
        /**
         * ⚠️ CZY SPIS JEST SPRZED OSTATNIEJ OPERACJI NA KATALOGU. Spis jedzie W sygnale życia
         * maszyny, a sygnał wychodzi PRZED wykonaniem zadań tego cyklu — więc skasowany przed
         * chwilą plik znika z listy dopiero przy następnym sygnale, do minuty później. Zgłoszone
         * jako „kliknąłem Usuń, w logach ok, a nadal jest na liście" i widziane tak samo
         * na warsztacie. Zamiast udawać, że listy nie ma, mówimy wprost, że jest sprzed operacji.
         */
        spisSprzedOperacji={(() => {
          const zmiany = (zadania.data ?? [])
            .filter(
              (z) =>
                (z.kind === 'KOPIA_LOKALNA' || z.kind === 'KOPIA_USUN' || z.kind === 'KOPIA_POBIERZ') &&
                z.status === 'DONE' &&
                z.finishedAt !== null,
            )
            .map((z) => new Date(z.finishedAt!).getTime());
          if (zmiany.length === 0) return false;
          const zmierzono = bazowi.find((a) => a.id === wybrany)?.diskAt;
          return zmierzono != null && new Date(zmierzono).getTime() < Math.max(...zmiany);
        })()}
        /**
         * ⚠️ SUMY KOPII JUŻ LEŻĄCYCH W MAGAZYNIE — po nich rozstrzygamy, czy jest co wysyłać.
         * Bierzemy WYŁĄCZNIE zadania, które mają jeszcze plik (`output.suma`): kopia przycięta
         * limitem zostawia wpis bez pliku, a taka nie jest „już po drugiej stronie".
         */
        trwa={trwa}
        wMagazynie={new Set(
          kopie.map((z) => (z.output as { suma?: string } | null)?.suma).filter((x): x is string => typeof x === 'string'),
        )}
      />

      {/*
        ⚠️ POTWIERDZENIE, BO TO JEST NIEODWRACALNE I BEZ KOSZA. Kopia bazy klienta nie ma po
        naszej stronie drugiego egzemplarza — pomyłkowe kliknięcie kosza kosztuje tyle, ile
        wart był ten plik w chwili, gdy okaże się potrzebny.
      */}
      <Dialog open={doUsuniecia !== null} onOpenChange={(o) => !o && setDoUsuniecia(null)}>
        {doUsuniecia && (
          <DialogContent
            title={t('kopie.usunTytul')}
            description={t('kopie.usunOpis').replace(
              '{kiedy}',
              new Date(doUsuniecia.finishedAt ?? doUsuniecia.createdAt).toLocaleString('pl-PL'),
            )}
          >
            <p className="text-sm text-muted-foreground">{t('kopie.usunWpis')}</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDoUsuniecia(null)} disabled={usun.isPending}>
                {t('common.cancel')}
              </Button>
              <Button variant="destructive" onClick={() => usun.mutate(doUsuniecia.id)} disabled={usun.isPending}>
                {usun.isPending && <Loader2 className="animate-spin" />}
                {t('kopie.usun')}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/*
        ⚠️⚠️ POTWIERDZENIE, BO TO NIEODWRACALNE. Paczka na półce to CZĘSTO JEDYNY egzemplarz
        kopii, jaki mamy — leży tam właśnie dlatego, że wyspy jeszcze nie ma i nikt jej nigdzie
        nie odtworzył.

        ⚠️ Osobne okno od tego wyżej, bo to inne zdanie: tam kasujemy plik zostawiając wpis
        („kto i kiedy zrobił kopię"), tutaj znika cały wiersz razem z plikiem.
      */}
      <Dialog open={paczkaDoUsuniecia !== null} onOpenChange={(o) => !o && setPaczkaDoUsuniecia(null)}>
        {paczkaDoUsuniecia && (
          <DialogContent title={t('poczek.usunTytul')} description={paczkaDoUsuniecia.nazwa}>
            <p className="text-sm text-muted-foreground">{t('poczek.usunTresc')}</p>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setPaczkaDoUsuniecia(null)}
                disabled={usunPaczke.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                disabled={usunPaczke.isPending}
                onClick={() => {
                  const id = paczkaDoUsuniecia.id;
                  setPaczkaDoUsuniecia(null);
                  usunPaczke.mutate(id);
                }}
              >
                {usunPaczke.isPending && <Loader2 className="animate-spin" />}
                {t('kopie.usun')}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/*
        ⚠️ MÓWIMY WPROST, CZEGO TU JESZCZE NIE MA — i aktualizujemy to zdanie, gdy coś dojdzie.
        Poprzednia wersja mówiła „przywrócenie bazy wymaga ręcznej pracy na maszynie"; odtwarzanie
        już jest, więc zostawienie tamtego tekstu byłoby gorsze niż jego brak: ekran twierdziłby,
        że nie umie czegoś, co ma pod przyciskiem obok.
      */}
      {/*
        ⚠️⚠️ NASZA STRONA ROZJAZDU, OBOK KOPII BAZY KLIENTA. Kopię SPS portal umie oddać od zawsze;
        swoich własnych danych — nie umiał, więc każde pytanie „czy portal ma to samo co master"
        kończyło się liczeniem sum i typowaniem. Stoi TUTAJ, bo tutaj człowiek przychodzi po plik.
      */}
      <KopiaPortalu groupId={groupId ?? ''} />

      <Card>
        <CardContent className="flex items-start gap-3 pt-5 text-sm text-muted-foreground">
          <ShieldQuestion className="mt-0.5 size-4 shrink-0" />
          <p>{t('kopie.wPlanie')}</p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * PRZYCISK „NA MASZYNĘ" — ściągnięcie kopii z magazynu na dysk maszyny klienta.
 *
 * ⚠️⚠️ ZAWSZE WIDOCZNY, TAKŻE GDY NIE DA SIĘ GO KLIKNĄĆ. Poprzednia wersja po prostu go nie
 * rysowała, gdy coś nie pasowało — a użytkownik zgłosił wtedy „nadal nie mamy pusha backupów
 * z serwera na maszynę", mimo że funkcja działała od dnia i była przetestowana. To jest sedno:
 * BRAK PRZYCISKU CZYTA SIĘ JAK BRAK FUNKCJI. Wyszarzony przycisk z powodem w dymku mówi, że
 * droga istnieje, tylko akurat teraz jest zamknięta — i dlaczego.
 */
function NaMaszyne({
  powod,
  pracuje,
  onKlik,
}: {
  /** `null` = można klikać. Reszta to nazwa powodu blokady, przetłumaczona na zdanie. */
  powod: 'brakMaszyny' | 'brakSumy' | 'juzTam' | 'trwa' | null;
  pracuje: boolean;
  onKlik: () => void;
}) {
  const { t } = useI18n();

  return (
    <Button
      size="sm"
      variant="outline"
      title={t(powod === null ? 'kopie.naMaszyneOpis' : `kopie.naMaszyneNie.${powod}`)}
      disabled={powod !== null || pracuje}
      onClick={onKlik}
    >
      {pracuje ? <Loader2 className="animate-spin" /> : <HardDrive />}
      {t('kopie.naMaszyne')}
    </Button>
  );
}

function StanKopii({ z }: { z: MachineTask }) {
  const { t } = useI18n();

  if (z.status === 'FAILED') {
    return (
      <span className="flex min-w-0 items-start gap-1.5 text-[var(--destructive)]">
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
        {/* ⚠️ POWÓD W CAŁOŚCI — to jedyna droga, którą człowiek zobaczy, co stało się na maszynie. */}
        <span className="min-w-0 break-words">{z.error ?? t('common.error')}</span>
      </span>
    );
  }

  if (z.status === 'DONE') {
    // „Gotowe" nie znaczy „jest co pobrać": trzymamy pięć ostatnich kopii, starsze kasujemy
    // razem z plikiem, zostawiając sam wpis w rejestrze.
    return z.output === null ? (
      <span className="text-muted-foreground">{z.note ?? t('kopia.plikUsuniety')}</span>
    ) : (
      <Badge variant="success">{t('kopia.gotowa')}</Badge>
    );
  }

  return (
    <span className="flex items-center gap-2 text-muted-foreground">
      <Loader2 className="size-3.5 animate-spin" />
      {/* Zdanie od agenta („pakuję kopię (104 MB)") mówi więcej niż sam procent. */}
      <span>{z.note ?? t('kopia.wKolejce')}</span>
      {z.progress !== null && <span className="tabular-nums">{z.progress}%</span>}
    </span>
  );
}

const MB = 1_048_576;
const nfMB = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 });
const mb = (b: number): string => `${nfMB.format(Math.round(b / MB))} MB`;
const gb = (b: number): string => `${new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 1 }).format(b / 1024 / MB)} GB`;

/**
 * KOPIE LEŻĄCE NA DYSKU MASZYNY — druga lista, obok kopii w magazynie.
 *
 * ⚠️ TO INNY ZBIÓR, NIE INNY WIDOK TEGO SAMEGO. W magazynie trzymamy pięć ostatnich paczek;
 * na maszynie klienta może ich leżeć dowolnie wiele i to one są pod ręką, gdy trzeba odtworzyć
 * bazę bez internetu. Dlatego osobna karta, a nie zakładka w tej samej tabeli.
 *
 * ⚠️ TRZY RÓŻNE „NIC", które ekran musi rozróżniać, bo wymagają różnych reakcji:
 *   • agent nie prowadzi kopii (maszyna podległa albo brak katalogu) — nie ma o co pytać,
 *   • agent nie zdążył jeszcze zmierzyć — poczekaj,
 *   • agent zmierzył i katalog jest pusty — zrób kopię.
 */
function KopieLokalne({
  agent,
  zadania,
  trwa,
  wMagazynie,
  spisSprzedOperacji,
}: {
  agent: AgentRow | null;
  /** Zadania wyspy — źródło paczek, o których portal wie z własnych wpisów. */
  zadania: readonly MachineTask[];
  trwa: boolean;
  wMagazynie: ReadonlySet<string>;
  /** Czy spis powstał PRZED ostatnią operacją na katalogu kopii — patrz uzasadnienie u wołającego. */
  spisSprzedOperacji: boolean;
}) {
  const { t } = useI18n();
  const { groupId } = useIslands();
  const toast = useToast();
  const qc = useQueryClient();

  /**
   * ⚠️ USUWANIE Z POTWIERDZENIEM, tak samo jak przy kopii w magazynie. Kopia lokalna bywa JEDYNYM
   * egzemplarzem — jeśli nie zdążyła pojechać w górę, po skasowaniu nie ma jej nigdzie.
   */
  const [doUsuniecia, setDoUsuniecia] = useState<{ nazwa: string; rozmiar: number } | null>(null);

  /**
   * ODTWORZENIE BAZY. ⚠️ Osobne okno, a nie przycisk działający od razu — bo trzeba podać nazwę
   * bazy docelowej, a to jest jedyna rzecz w całym ekranie, której nie da się wywnioskować z danych.
   */
  const [doOdtworzenia, setDoOdtworzenia] = useState<{ nazwa: string } | null>(null);
  const [bazaDocelowa, setBazaDocelowa] = useState('');
  /** Ten sam wzorzec co w API i w agencie: nazwa wchodzi do treści `RESTORE DATABASE [...]`. */
  const nazwaBazyOk = /^[A-Za-z_][A-Za-z0-9_$-]*$/.test(bazaDocelowa.trim());

  const odtworz = useMutation({
    mutationFn: () => zlecOdtworzenie(agent!.id, groupId!, doOdtworzenia!.nazwa, bazaDocelowa.trim()),
    onSuccess: async () => {
      setDoOdtworzenia(null);
      toast.success(t('kopie.odtworzZlecone'));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const usun = useMutation({
    mutationFn: (nazwa: string) => zlecUsuniecieKopiiLokalnej(agent!.id, groupId!, nazwa),
    onSuccess: async () => {
      setDoUsuniecia(null);
      toast.success(t('kopie.usuniecieZlecone'));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const wyslij = useMutation({
    mutationFn: (nazwa: string) => zlecWyslanieKopii(agent!.id, groupId!, nazwa),
    onSuccess: async () => {
      toast.success(t('kopie.wyslanieZlecone'));
      await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * ⚠️ PROŚBA O ŚWIEŻY SPIS PRZY WEJŚCIU NA EKRAN, ale bez obietnicy natychmiastowości: spis
   * jedzie W sygnale życia agenta, a ta prośba wraca dopiero w ODPOWIEDZI na niego, więc wynik
   * pojawia się po dwóch cyklach. Ekran mówi to wprost zamiast kręcić kółkiem i udawać.
   */
  useEffect(() => {
    if (groupId === null) return;
    void odswiezSpisKopii(groupId).catch(() => undefined);
  }, [groupId]);

  if (agent === null) return null;
  /**
   * ⚠️ SPIS AGENTA UZUPEŁNIONY O PACZKI Z ZADAŃ PORTALU — pełne uzasadnienie w `kopie-na-dysku.ts`.
   * W skrócie: na świeżym masterze w chmurze cykl agenta nie udaje się NIGDY (bazy jeszcze nie ma),
   * a spis jedzie tylko w sygnale życia UDANEGO cyklu — więc pobrana paczka byłaby niewidoczna,
   * a to przy niej stoi jedyny przycisk, którym tę bazę da się w ogóle założyć.
   */
  const { lista, zZadan } = paczkiNaDysku({
    spis: agent.localBackups,
    spisZ: agent.diskAt,
    zadania,
    agentId: agent.id,
  });
  const wolne = agent.diskFreeBytes ?? null;
  const calkowite = agent.diskTotalBytes ?? null;
  const udzialWolnego = wolne !== null && calkowite !== null && calkowite > 0 ? wolne / calkowite : null;

  return (
    <Card>
      <CardContent className="space-y-4 pt-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">{t('kopie.lokalneTytul')}</h2>
            <p className="text-xs text-muted-foreground">{t('kopie.lokalneOpis')}</p>
          </div>
          {agent.diskAt !== null && agent.diskAt !== undefined && (
            <span className="text-xs text-muted-foreground">
              {t('kopie.zmierzono').replace('{kiedy}', new Date(agent.diskAt).toLocaleString('pl-PL'))}
            </span>
          )}
        </div>

        {/*
          ⚠️ MÓWIMY, ŻE LISTA JEST SPRZED OPERACJI, zamiast pozwolić jej wyglądać na zepsutą.
          Skasowany przed chwilą plik stoi tu jeszcze przez jeden cykl maszyny — i dokładnie tak
          to zgłoszono: „w logach wygląda ok, ale nadal jest na liście".
        */}
        {spisSprzedOperacji && (
          <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            {t('kopie.spisSprzed')}
          </p>
        )}

        {/*
          LICZNIK MIEJSCA. ⚠️ Podpisany LITERĄ, KTÓRĄ PODAŁ AGENT — wolumen wylicza on ze ścieżki
          swojego katalogu, bo nie każda maszyna trzyma agenta na `C:`.
        */}
        {wolne !== null && calkowite !== null ? (
          <div className="space-y-1">
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-muted-foreground">
                {t('kopie.dysk').replace('{wolumen}', agent.diskVolume ?? '?')}
              </span>
              <span className="tabular-nums">
                {t('kopie.wolne').replace('{wolne}', gb(wolne)).replace('{calkowite}', gb(calkowite))}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  'h-full rounded-full',
                  udzialWolnego !== null && udzialWolnego < 0.1 ? 'bg-destructive' : 'bg-primary',
                )}
                style={{ width: `${Math.max(2, Math.round((1 - (udzialWolnego ?? 0)) * 100))}%` }}
              />
            </div>
            {/*
              ⚠️ OSTRZEŻENIE PRZY 10 %, bo to jest próg, przy którym agent ODMÓWI kolejnej kopii —
              i lepiej, żeby człowiek dowiedział się o tym z ekranu niż z nieudanej operacji.
            */}
            {udzialWolnego !== null && udzialWolnego < 0.1 && (
              <p className="text-xs text-destructive">{t('kopie.maloMiejsca')}</p>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{t('kopie.dyskNieznany')}</p>
        )}

        {lista === null ? (
          <p className="text-sm text-muted-foreground">{t('kopie.lokalneBrakPomiaru')}</p>
        ) : lista.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('kopie.lokalnePusto')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="pb-2 pr-3 font-medium">{t('kopie.kiedy')}</th>
                  <th className="pb-2 pr-3 font-medium">{t('kopie.plik')}</th>
                  <th className="pb-2 pr-3 text-right font-medium">{t('kopie.rozmiar')}</th>
                  <th className="pb-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {lista.map((k) => {
                  /**
                   * ⚠️ KONFLIKT ROZSTRZYGA SUMA, NIE NAZWA ANI ROZMIAR. Dwa zrzuty tej samej bazy
                   * z tej samej minuty mają tę samą nazwę i podobny rozmiar, a różną zawartość —
                   * porównanie po nazwie odmawiałoby wysyłki czegoś, czego w magazynie nie ma.
                   */
                  const juzTam = wMagazynie.has(k.suma);
                  return (
                    <tr key={k.suma} className="border-b border-border/60 last:border-0">
                      <td className="py-2 pr-3 tabular-nums">{new Date(k.utworzono).toLocaleString('pl-PL')}</td>
                      <td className="py-2 pr-3">
                        <code className="text-xs">{k.nazwa}</code>
                        {/* Skrót sumy — do porównania z kopią w magazynie bez wchodzenia w szczegóły. */}
                        <span className="ml-2 text-[11px] text-muted-foreground" title={k.suma}>
                          {k.suma.slice(0, 8)}
                        </span>
                        {/*
                          ⚠️ MÓWIMY, SKĄD TEN WIERSZ SIĘ WZIĄŁ, bo nie z pomiaru maszyny. Paczkę
                          położył tam portal swoim zadaniem i zna jej nazwę, rozmiar oraz sumę
                          z własnego wpisu — ale dopóki agent nie przyśle spisu, nikt jej nie
                          POLICZYŁ na dysku. Wiersz bez tej różnicy obiecywałby pomiar, którego
                          nie było; z nią mówi tyle, ile wiemy, i ani słowa więcej.
                        */}
                        {zZadan.has(k.suma) && (
                          <Badge variant="neutral" className="ml-2" title={t('kopie.zZadaniaOpis')}>
                            {t('kopie.zZadania')}
                          </Badge>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{mb(k.rozmiar)}</td>
                      <td className="py-2 text-right">
                        {/*
                          ⚠️ PRZYCISK ZOSTAJE, TYLKO SIĘ WYSZARZA — tak samo jak „Na maszynę" po
                          drugiej stronie. Poprzednio zamiast niego pojawiał się sam napis „jest już
                          w magazynie", więc w kolumnie akcji raz był przycisk, a raz tekst — i nie
                          dało się na pierwszy rzut oka powiedzieć, czy wysyłka w tę stronę w ogóle
                          istnieje. Powód blokady siedzi w dymku, nie zamiast przycisku.
                        */}
                        <Button
                          size="sm"
                          variant="outline"
                          title={t(juzTam ? 'kopie.wyslijNie.juzTam' : 'kopie.wyslijDoMagazynuOpis')}
                          disabled={juzTam || wyslij.isPending || trwa}
                          onClick={() => wyslij.mutate(k.nazwa)}
                        >
                          {wyslij.isPending && wyslij.variables === k.nazwa ? (
                            <Loader2 className="animate-spin" />
                          ) : (
                            <Upload />
                          )}
                          {t('kopie.wyslijDoMagazynu')}
                        </Button>
                        {/*
                          ⚠️ ODTWORZENIE TYLKO STĄD, Z KOPII LEŻĄCEJ NA MASZYNIE — nie z listy
                          w magazynie. `RESTORE` czyta plik z dysku serwera SQL, więc paczka
                          z magazynu musi najpierw pojechać na maszynę („wrzuć na komputer"),
                          a dopiero potem da się ją odtworzyć. Przycisk przy kopii w magazynie
                          obiecywałby jeden krok tam, gdzie są dwa.
                        */}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-1"
                          aria-label={t('kopie.odtworz')}
                          title={t('kopie.odtworzOpis')}
                          disabled={trwa}
                          onClick={() => {
                            setBazaDocelowa(bazaZNazwyPaczki(k.nazwa));
                            setDoOdtworzenia({ nazwa: k.nazwa });
                          }}
                        >
                          <DatabaseBackup />
                        </Button>
                        {/*
                          ⚠️ KOSZ ZAWSZE, TAKŻE PRZY KOPII JUŻ WYSŁANEJ. Miejsce na dysku maszyny
                          jest ograniczone, a wysłanie w górę go nie zwalnia — plik zostaje.
                          Bez tego przycisku dysk klienta da się wyłącznie zapełniać.
                        */}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-1"
                          aria-label={t('kopie.usun')}
                          title={t('kopie.usun')}
                          disabled={usun.isPending || trwa}
                          onClick={() => setDoUsuniecia({ nazwa: k.nazwa, rozmiar: k.rozmiar })}
                        >
                          <Trash2 />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/*
          ODTWORZENIE BAZY — okno z jednym polem, ale to pole decyduje o wszystkim.
        */}
        <Dialog open={doOdtworzenia !== null} onOpenChange={(o) => !o && setDoOdtworzenia(null)}>
          {doOdtworzenia && (
            <DialogContent
              title={t('kopie.odtworzTytul')}
              description={t('kopie.odtworzOkno').replace('{plik}', doOdtworzenia.nazwa)}
            >
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground" htmlFor="baza-docelowa">
                  {t('kopie.odtworzPole')}
                </label>
                <Input
                  id="baza-docelowa"
                  value={bazaDocelowa}
                  onChange={(e) => setBazaDocelowa(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                />
                {/*
                  ⚠️ WARUNEK NAPISANY WPROST, ZANIM KTOŚ KLIKNIE. Odtwarzamy WYŁĄCZNIE na nazwę,
                  której na serwerze nie ma — sprawdza to agent, więc bez tego zdania odmowa
                  przychodzi dopiero jako nieudane zadanie, kilka minut później i bez kontekstu.
                */}
                <p className="text-xs text-muted-foreground">{t('kopie.odtworzWarunek')}</p>
                {bazaDocelowa.trim() !== '' && !nazwaBazyOk && (
                  <p className="text-xs text-destructive">{t('kopie.odtworzZlaNazwa')}</p>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDoOdtworzenia(null)} disabled={odtworz.isPending}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={() => odtworz.mutate()} disabled={odtworz.isPending || !nazwaBazyOk}>
                  {odtworz.isPending && <Loader2 className="animate-spin" />}
                  {t('kopie.odtworz')}
                </Button>
              </DialogFooter>
            </DialogContent>
          )}
        </Dialog>

        <Dialog open={doUsuniecia !== null} onOpenChange={(o) => !o && setDoUsuniecia(null)}>
          {doUsuniecia && (
            <DialogContent
              title={t('kopie.usunLokalnaTytul')}
              description={t('kopie.usunLokalnaOpis')
                .replace('{plik}', doUsuniecia.nazwa)
                .replace('{mb}', mb(doUsuniecia.rozmiar))}
            >
              {/*
                ⚠️ MÓWIMY, CZY TA KOPIA JEST JESZCZE GDZIEŚ INDZIEJ. To jedyna informacja, która
                zmienia wagę tego kliknięcia: kopia leżąca też w magazynie jest do odtworzenia,
                a ta wyłącznie lokalna znika bezpowrotnie.
              */}
              <p className="text-sm text-muted-foreground">{t('kopie.usunLokalnaSkutek')}</p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDoUsuniecia(null)} disabled={usun.isPending}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => usun.mutate(doUsuniecia.nazwa)}
                  disabled={usun.isPending}
                >
                  {usun.isPending && <Loader2 className="animate-spin" />}
                  {t('kopie.usun')}
                </Button>
              </DialogFooter>
            </DialogContent>
          )}
        </Dialog>
      </CardContent>
    </Card>
  );
}
